/**
 * Shared adapter implementation. Application code should import from
 * project-state-adapter.ts, not from this file.
 */

import type { ProjectSnapshot, ProjectStateAdapterPublic } from '../project-state-adapter';

type Listener = () => void;

const EMPTY_SNAPSHOT: ProjectSnapshot = Object.freeze({
  status: 'idle',
  projectId: null,
  project: null,
  source: 'unknown',
});

class ProjectStateAdapterImpl implements ProjectStateAdapterPublic {
  private snapshot: ProjectSnapshot = EMPTY_SNAPSHOT;
  private listeners = new Set<Listener>();

  getSnapshot(): ProjectSnapshot {
    return this.snapshot;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Internal: only ProjectStateAdapterBridge (via project-state-adapter.internal) may call this. */
  _setSnapshot(next: ProjectSnapshot) {
    // Cheap equality guard to reduce spam updates.
    const prev = this.snapshot;
    if (
      prev.status === next.status &&
      prev.projectId === next.projectId &&
      prev.sharedFolderId === next.sharedFolderId &&
      prev.project === next.project &&
      prev.isAdmin === next.isAdmin &&
      prev.isCompanyAdmin === next.isCompanyAdmin &&
      prev.isOrganizationAdmin === next.isOrganizationAdmin &&
      prev.source === next.source
    ) {
      return;
    }

    // Freeze to discourage accidental mutation by consumers.
    this.snapshot = Object.freeze(next);
    this.listeners.forEach((l) => l());
  }
}

export const projectStateAdapter = new ProjectStateAdapterImpl();
